import * as THREE from 'three';

export type ShotType =
  | 'chase_tracking'
  | 'drift_wide'
  | 'drift_orbit'
  | 'drift_low_sweep'
  | 'drift_reverse_track'
  | 'drift_crane_up'
  | 'drift_ground_level'
  | 'drift_360_spin'
  | 'nitro_low_angle'
  | 'nitro_tunnel_rush'
  | 'air_hero'
  | 'air_bullet_time'
  | 'air_ground_anticipation'
  | 'cop_two_shot'
  | 'cop_helicopter'
  | 'cop_bumper_cam'
  | 'cop_chase_wide'
  | 'cop_dramatic_pursuit'
  | 'cop_ground_level'
  | 'cop_360_orbit'
  | 'cop_dolly_zoom'
  | 'cop_crane_sweep'
  | 'impact_reaction'
  | 'speed_rush'
  | 'speed_flyby'
  | 'speed_worm_eye'
  | 'speed_360_orbit'
  | 'hood_cam'
  | 'near_miss'
  | 'tire_orbit'
  | 'start_tire_reveal'
  | 'helicopter_reveal'
  | 'helicopter_swoop'
  | 'helicopter_chase'
  | 'pursuit_breaker_cam'
  | 'victory_celebration';

export interface DirectorInput {
  carPosition: THREE.Vector3;
  carVelocity: THREE.Vector3;
  carRotation: number;
  speedKmh: number;
  isDrifting: boolean;
  driftAngle: number;
  isNitroActive: boolean;
  isAirborne: boolean;
  timeSinceLastCollision: number;
  collisionImpactForce: number;
  copProximity: number;
  bustProgress: number;
  timeRemaining: number;
  extractionDirection: THREE.Vector3;
  /** Helicopter position if active, null otherwise */
  helicopterPosition?: THREE.Vector3 | null;
}

export interface CameraDirectorOutput {
  shot: ShotType;
  timeScale: number;
  chromaticAberration: number;
  radialBlurIntensity: number;
  vignette: number;
  redTint: number;
  shake: THREE.Vector3;
  motionBlurAmount: number;
  filmGrain: number;
  depthOfField: number;
  anamorphicStretch: number;
}

export class CameraDirector {
  private currentShot: ShotType = 'chase_tracking';
  private shotTimer = 0;
  private totalTime = 0;

  private readonly camPos = new THREE.Vector3();
  private readonly camTarget = new THREE.Vector3();
  private camFov = 50;
  private camDutch = 0;

  private readonly targetPos = new THREE.Vector3();
  private readonly targetLook = new THREE.Vector3();
  private targetFov = 50;
  private targetDutch = 0;

  private driftSide = 1;
  private collisionSide = 1;
  private copShotCooldown = 0;
  private speedRushCooldown = 0;
  private hoodCamCooldown = 0;
  private helicopterRevealCooldown = 0;

  private initialized = false;
  private shakeTrauma = 0;

  private lastShotChange = 0;
  private shotHoldTime = 0;

  private driftShotIndex = 0;
  private driftShotCooldown = 0;
  private driftStartTime = 0;
  private wasDrifting = false;
  private driftPeakAngle = 0;
  private driftOrbitAngle = 0;
  private driftEntryPos = new THREE.Vector3();
  private cinematicSlowMo = 0;
  private slowMoTarget = 0;

  private speedShotIndex = 0;
  private speedShotCooldown = 0;
  private airShotIndex = 0;
  private flybyStartPos = new THREE.Vector3();
  private flybyDirection = new THREE.Vector3();
  private nearMissCooldown = 0;

  private tireOrbitCooldown = 0;
  private tireOrbitAngle = 0;

  private hasTriggeredStartReveal = false;
  private forcedShot: ShotType | null = null;
  private forcedShotDuration = 0;
  private pursuitBreakerPos = new THREE.Vector3();
  private readonly tmpVictoryExt = new THREE.Vector3();
  private readonly tmpVictorySide = new THREE.Vector3();
  private readonly tmpVictoryLook = new THREE.Vector3();

  update(
    input: DirectorInput,
    camera: THREE.PerspectiveCamera,
    dt: number,
  ): CameraDirectorOutput {
    this.shotTimer += dt;
    this.totalTime += dt;
    if (this.copShotCooldown > 0) this.copShotCooldown -= dt;
    if (this.speedRushCooldown > 0) this.speedRushCooldown -= dt;
    if (this.hoodCamCooldown > 0) this.hoodCamCooldown -= dt;
    if (this.driftShotCooldown > 0) this.driftShotCooldown -= dt;
    if (this.tireOrbitCooldown > 0) this.tireOrbitCooldown -= dt;

    this.updateDriftTracking(input);

    // Handle forced shots (e.g. pursuit breaker cam)
    if (this.forcedShot) {
      this.forcedShotDuration -= dt;
      if (this.forcedShotDuration <= 0) {
        this.forcedShot = null;
        this.currentShot = 'chase_tracking';
        this.shotTimer = 0;
      }
    }

    if (!this.forcedShot) {
      const newShot = this.pickShot(input);
      if (newShot !== this.currentShot && this.totalTime - this.lastShotChange > this.shotHoldTime) {
        this.onShotChange(newShot, input);
      }
    }

    this.computeShotCamera(input);

    if (!this.initialized) {
      this.camPos.copy(this.targetPos);
      this.camTarget.copy(this.targetLook);
      this.camFov = this.targetFov;
      this.camDutch = this.targetDutch;
      this.initialized = true;
    }

    const sm = this.getShotSmoothing();
    const posLerp = 1 - Math.exp(-sm.pos * dt);
    const lookLerp = 1 - Math.exp(-sm.look * dt);
    const fovLerp = 1 - Math.exp(-sm.fov * dt);

    this.camPos.lerp(this.targetPos, posLerp);
    this.camTarget.lerp(this.targetLook, lookLerp);
    this.camFov = THREE.MathUtils.lerp(this.camFov, this.targetFov, fovLerp);
    this.camDutch = THREE.MathUtils.lerp(this.camDutch, this.targetDutch, fovLerp);

    this.camPos.y = Math.max(0.6, this.camPos.y);

    if (input.collisionImpactForce > 0.3 && input.timeSinceLastCollision < 0.1) {
      this.shakeTrauma = Math.min(1, this.shakeTrauma + input.collisionImpactForce * 0.6);
    }
    if (input.speedKmh > 120) {
      this.shakeTrauma = Math.max(this.shakeTrauma, 0.05 + (input.speedKmh - 120) / 500);
    }
    this.shakeTrauma = Math.max(0, this.shakeTrauma - dt * 1.8);

    const shake = this.computeShake();

    camera.position.copy(this.camPos).add(shake);
    camera.lookAt(this.camTarget);
    camera.fov = this.camFov;
    camera.updateProjectionMatrix();
    camera.rotateZ(this.camDutch);

    this.cinematicSlowMo = THREE.MathUtils.lerp(
      this.cinematicSlowMo,
      this.slowMoTarget,
      1 - Math.exp(-8 * dt),
    );

    const isDriftShot = this.isDriftCinematicShot();
    const victoryShot = this.currentShot === 'victory_celebration';
    const timeScale = this.getTimeScale(input);

    return {
      shot: this.currentShot,
      timeScale,
      chromaticAberration: this.getChromaticAberration(input),
      radialBlurIntensity: this.getBlur(input),
      vignette: this.getVignette(input),
      redTint: this.currentShot === 'pursuit_breaker_cam' ? 0.12 : this.currentShot === 'cop_two_shot' ? 0.06 : 0,
      shake,
      motionBlurAmount: this.getMotionBlur(input),
      filmGrain: isDriftShot ? 0.08 : victoryShot ? 0.065 : 0.02,
      depthOfField: this.getDepthOfField(input),
      anamorphicStretch: isDriftShot ? 1.15 : victoryShot ? 1.11 : 1.0,
    };
  }

  private updateDriftTracking(input: DirectorInput): void {
    if (input.isDrifting && input.driftAngle > 15) {
      if (!this.wasDrifting) {
        this.driftStartTime = this.totalTime;
        this.driftEntryPos.copy(input.carPosition);
        this.driftOrbitAngle = input.carRotation + Math.PI;
        this.slowMoTarget = 0.5;
      }
      this.driftPeakAngle = Math.max(this.driftPeakAngle, input.driftAngle);
      this.wasDrifting = true;
    } else {
      if (this.wasDrifting && this.driftPeakAngle > 25) {
        this.slowMoTarget = 0;
      }
      this.wasDrifting = false;
      this.driftPeakAngle = 0;
      this.slowMoTarget = 0;
    }
  }

  private isDriftCinematicShot(): boolean {
    return (
      this.currentShot === 'drift_wide' ||
      this.currentShot === 'drift_orbit' ||
      this.currentShot === 'drift_low_sweep' ||
      this.currentShot === 'drift_reverse_track' ||
      this.currentShot === 'drift_crane_up' ||
      this.currentShot === 'drift_ground_level' ||
      this.currentShot === 'drift_360_spin' ||
      this.currentShot === 'tire_orbit'
    );
  }

  private getMotionBlur(input: DirectorInput): number {
    if (this.isDriftCinematicShot()) {
      const intensity = Math.min(1, input.driftAngle / 45);
      return 0.4 + intensity * 0.4;
    }
    if (this.currentShot === 'victory_celebration') {
      return 0.48 + Math.sin(this.shotTimer * 2.2) * 0.14;
    }
    if (this.currentShot === 'nitro_low_angle') return 0.6;
    if (this.currentShot === 'speed_rush') return 0.5;
    if (input.speedKmh > 100) return 0.2 + (input.speedKmh - 100) / 200;
    return 0.1;
  }

  private getDepthOfField(input: DirectorInput): number {
    if (this.currentShot === 'drift_ground_level') return 0.8;
    if (this.currentShot === 'drift_low_sweep') return 0.6;
    if (this.isDriftCinematicShot()) return 0.4;
    if (this.currentShot === 'victory_celebration') {
      return 0.36 + Math.sin(this.shotTimer * 1.4) * 0.12;
    }
    if (input.speedKmh > 150) return 0.3;
    return 0.1;
  }

  private computeShake(): THREE.Vector3 {
    const intensity = this.shakeTrauma * this.shakeTrauma;
    const time = this.totalTime * 30;

    const x = Math.sin(time * 1.1) * Math.cos(time * 0.7) * intensity * 0.2;
    const y = Math.sin(time * 1.3) * Math.cos(time * 0.9) * intensity * 0.15;
    const z = Math.sin(time * 0.8) * intensity * 0.08;

    return new THREE.Vector3(x, y, z);
  }

  private getTimeScale(input: DirectorInput): number {
    if (this.currentShot === 'pursuit_breaker_cam') {
      if (this.shotTimer < 0.3) return 0.2;
      if (this.shotTimer < 0.6) return 0.35;
      return 0.6 + Math.min(0.4, (this.shotTimer - 0.6) * 0.7);
    }
    if (this.currentShot === 'start_tire_reveal') {
      if (this.shotTimer < 1.0) return 0.25 + this.shotTimer * 0.15;
      if (this.shotTimer < 2.0) return 0.4 + (this.shotTimer - 1.0) * 0.3;
      return 0.7 + Math.min(0.3, (this.shotTimer - 2.0) * 0.2);
    }
    if (this.currentShot === 'air_hero' && this.shotTimer < 1.5) {
      return 0.4 + this.shotTimer * 0.4;
    }
    if (this.currentShot === 'air_bullet_time') {
      if (this.shotTimer < 0.4) return 0.15;
      if (this.shotTimer < 1.0) return 0.2 + (this.shotTimer - 0.4) * 0.5;
      return 0.5 + Math.min(0.5, (this.shotTimer - 1.0) * 0.3);
    }
    if (this.currentShot === 'air_ground_anticipation') {
      return 0.35 + this.shotTimer * 0.2;
    }
    if (this.currentShot === 'impact_reaction' && this.shotTimer < 0.15) {
      return 0.3;
    }
    if (this.currentShot === 'near_miss') {
      if (this.shotTimer < 0.3) return 0.2;
      return 0.3 + this.shotTimer * 0.35;
    }
    if (this.currentShot === 'speed_flyby') {
      const phase = Math.min(1, this.shotTimer / 1.5);
      if (phase < 0.3) return 0.4;
      if (phase < 0.7) return 0.5 + (phase - 0.3) * 0.75;
      return 0.8 + (phase - 0.7) * 0.66;
    }
    if (this.currentShot === 'speed_worm_eye') {
      if (this.shotTimer < 0.2) return 0.25;
      return 0.35 + Math.min(0.5, this.shotTimer * 0.25);
    }
    if (this.currentShot === 'nitro_tunnel_rush') {
      return 0.6 + Math.sin(this.shotTimer * 2) * 0.15;
    }
    if (this.currentShot === 'cop_helicopter') {
      return 0.7;
    }
    if (this.currentShot === 'cop_bumper_cam') {
      return 0.55 + this.shotTimer * 0.15;
    }

    if (this.isDriftCinematicShot()) {
      const driftDuration = this.totalTime - this.driftStartTime;
      const intensity = Math.min(1, input.driftAngle / 50);

      if (this.currentShot === 'drift_orbit') {
        if (this.shotTimer < 0.3) return 0.25 + this.shotTimer * 0.5;
        if (this.shotTimer < 1.5) return 0.4 + intensity * 0.2;
        return 0.6 + this.shotTimer * 0.15;
      }

      if (this.currentShot === 'drift_low_sweep') {
        const sweepPhase = Math.sin(this.shotTimer * Math.PI * 0.5);
        return 0.35 + sweepPhase * 0.35 + intensity * 0.15;
      }

      if (this.currentShot === 'drift_ground_level') {
        if (this.shotTimer < 0.2) return 0.2;
        return 0.3 + Math.min(0.5, this.shotTimer * 0.25);
      }

      if (this.currentShot === 'drift_crane_up') {
        const cranePhase = Math.min(1, this.shotTimer / 2.0);
        return 0.4 + cranePhase * 0.4;
      }

      if (this.currentShot === 'drift_reverse_track') {
        return 0.5 + intensity * 0.3;
      }

      if (this.currentShot === 'drift_360_spin') {
        if (this.shotTimer < 0.3) return 0.15;
        if (this.shotTimer < 1.0) return 0.25 + this.shotTimer * 0.2;
        return 0.45 + intensity * 0.25;
      }

      if (this.currentShot === 'tire_orbit') {
        if (this.shotTimer < 0.3) return 0.12;
        if (this.shotTimer < 0.8) return 0.2 + this.shotTimer * 0.15;
        return 0.35 + intensity * 0.2;
      }

      if (driftDuration < 0.5) {
        return 0.35 + driftDuration * 0.3;
      }
      return 0.5 + intensity * 0.3;
    }

    if (this.currentShot === 'cop_chase_wide') {
      const sweepPhase = Math.abs(Math.sin(this.shotTimer * Math.PI * 0.35));
      if (this.shotTimer < 0.3) return 0.2;
      return 0.35 + sweepPhase * 0.25;
    }
    if (this.currentShot === 'cop_dramatic_pursuit') {
      if (this.shotTimer < 0.3) return 0.18;
      if (this.shotTimer < 1.0) return 0.25 + this.shotTimer * 0.12;
      return 0.4 + Math.sin(this.shotTimer * 1.5) * 0.1;
    }
    if (this.currentShot === 'cop_ground_level') {
      if (this.shotTimer < 0.25) return 0.12;
      if (this.shotTimer < 0.8) return 0.2 + this.shotTimer * 0.15;
      return 0.35 + Math.min(0.35, (this.shotTimer - 0.8) * 0.25);
    }
    if (this.currentShot === 'cop_360_orbit') {
      if (this.shotTimer < 0.4) return 0.15;
      if (this.shotTimer < 1.2) return 0.22 + this.shotTimer * 0.1;
      return 0.35 + Math.sin(this.shotTimer * 1.8) * 0.12;
    }
    if (this.currentShot === 'cop_dolly_zoom') {
      const phase = Math.min(1, this.shotTimer / 2.2);
      if (this.shotTimer < 0.25) return 0.12;
      if (this.shotTimer < 0.8) return 0.18 + this.shotTimer * 0.12;
      return 0.28 + phase * 0.22 + Math.sin(this.shotTimer) * 0.08;
    }
    if (this.currentShot === 'cop_crane_sweep') {
      const phase = Math.min(1, this.shotTimer / 2.8);
      const ease = phase * phase * (3 - 2 * phase);
      if (phase < 0.15) return 0.15;
      return 0.25 + ease * 0.45;
    }

    if (this.currentShot === 'speed_360_orbit') {
      if (this.shotTimer < 0.3) return 0.3;
      return 0.45 + Math.sin(this.shotTimer * 2) * 0.15;
    }
    if (this.currentShot === 'victory_celebration') {
      const s = this.shotTimer;
      if (s < 0.42) return 0.1 + s * 0.28;
      if (s < 1.28) return 0.2 + (s - 0.42) * 0.11;
      if (s < 2.1) return 0.3 + Math.sin(s * 2.35) * 0.12;
      if (s < 2.82) return 0.42 + (s - 2.1) * 0.28;
      return Math.min(0.92, 0.62 + (s - 2.82) * 0.55);
    }

    return 1;
  }

  private getChromaticAberration(input: DirectorInput): number {
    if (this.isDriftCinematicShot()) {
      const intensity = Math.min(1, input.driftAngle / 45);
      if (this.currentShot === 'drift_ground_level') return 0.008 + intensity * 0.006;
      if (this.currentShot === 'drift_low_sweep') return 0.006 + intensity * 0.004;
      return 0.004 + intensity * 0.004;
    }
    if (this.isAirCinematicShot()) {
      if (this.currentShot === 'air_bullet_time') return 0.01;
      return 0.005;
    }
    if (this.isSpeedCinematicShot()) {
      if (this.currentShot === 'speed_worm_eye') return 0.012;
      if (this.currentShot === 'speed_flyby') return 0.008;
      return 0.005;
    }
    if (this.currentShot === 'near_miss') return 0.008;
    if (this.currentShot === 'pursuit_breaker_cam') return 0.015;
    if (this.currentShot === 'victory_celebration') {
      const s = this.shotTimer;
      return (
        0.014 +
        Math.sin(s * 3.2) * 0.008 +
        (s < 0.55 ? 0.01 : 0) +
        (s > 1.35 && s < 1.55 ? 0.006 : 0)
      );
    }
    if (this.currentShot === 'nitro_tunnel_rush') return 0.01;
    if (this.currentShot === 'nitro_low_angle') return 0.006;
    if (this.currentShot === 'cop_bumper_cam') return 0.004;
    if (this.currentShot === 'impact_reaction') return 0.005;
    if (input.speedKmh > 150) return 0.002;
    return 0;
  }

  private pickShot(input: DirectorInput): ShotType {
    if (this.nearMissCooldown > 0) this.nearMissCooldown -= 0.016;
    if (this.helicopterRevealCooldown > 0) this.helicopterRevealCooldown -= 0.016;

    if (!this.hasTriggeredStartReveal && input.speedKmh > 5 && input.speedKmh < 40) {
      this.hasTriggeredStartReveal = true;
      return 'start_tire_reveal';
    }
    if (this.currentShot === 'start_tire_reveal' && this.shotTimer < 3.8) {
      return 'start_tire_reveal';
    }

    if (input.timeSinceLastCollision < 0.08 && input.collisionImpactForce > 0.5) {
      return 'impact_reaction';
    }
    if (this.currentShot === 'impact_reaction' && this.shotTimer < 0.35) {
      return 'impact_reaction';
    }

    if (
      input.timeSinceLastCollision < 0.15 &&
      input.collisionImpactForce > 0.1 &&
      input.collisionImpactForce < 0.4 &&
      this.nearMissCooldown <= 0
    ) {
      this.nearMissCooldown = 5;
      return 'near_miss';
    }
    if (this.currentShot === 'near_miss' && this.shotTimer < 1.2) {
      return 'near_miss';
    }

    if (input.isAirborne && input.speedKmh > 50) {
      return this.pickAirShot(input);
    }
    if (this.isAirCinematicShot() && this.shotTimer < this.getAirShotMinHold()) {
      return this.currentShot;
    }

    if (input.isNitroActive && !input.isDrifting) {
      return this.pickNitroShot(input);
    }
    if (this.isNitroCinematicShot() && this.shotTimer < 1.5) {
      return this.currentShot;
    }

    if (input.isDrifting && input.driftAngle > 12) {
      return this.pickDriftShot(input);
    }

    if (this.isDriftCinematicShot()) {
      const minHold = this.getDriftShotMinHold();
      if (this.shotTimer < minHold) {
        return this.currentShot;
      }
    }

    // Helicopter cinematic shots - show helicopter when active (but not during start sequence)
    if (input.helicopterPosition && 
        this.helicopterRevealCooldown <= 0 && 
        this.totalTime > 4 && // Don't trigger in first 4 seconds
        Math.random() < 0.04) { // 4% chance per frame - MORE FREQUENT!
      this.helicopterRevealCooldown = 5; // 5 second cooldown - SHORTER!
      // Pick a random helicopter shot type for variety
      const heliShots: ShotType[] = ['helicopter_reveal', 'helicopter_swoop', 'helicopter_chase'];
      return heliShots[Math.floor(Math.random() * heliShots.length)]!;
    }
    if ((this.currentShot === 'helicopter_reveal' || 
         this.currentShot === 'helicopter_swoop' ||
         this.currentShot === 'helicopter_chase') && this.shotTimer < 2.5) {
      return this.currentShot;
    }

    if (input.copProximity > 0.6 && this.copShotCooldown <= 0) {
      return this.pickCopShot(input);
    }
    if (this.isCopCinematicShot() && this.shotTimer < this.getCopShotMinHold()) {
      return this.currentShot;
    }

    if (input.speedKmh > 140 && this.speedShotCooldown <= 0 && Math.random() < 0.015) {
      return this.pickSpeedShot(input);
    }
    if (this.isSpeedCinematicShot() && this.shotTimer < this.getSpeedShotMinHold()) {
      return this.currentShot;
    }

    if (input.speedKmh > 100 && this.hoodCamCooldown <= 0 && Math.random() < 0.005) {
      return 'hood_cam';
    }
    if (this.currentShot === 'hood_cam' && this.shotTimer < 1.5) {
      return 'hood_cam';
    }

    return 'chase_tracking';
  }


  private pickDriftShot(input: DirectorInput): ShotType {
    const driftDuration = this.totalTime - this.driftStartTime;
    const intensity = Math.min(1, input.driftAngle / 45);

    if (this.isDriftCinematicShot() && this.shotTimer < this.getDriftShotMinHold()) {
      return this.currentShot;
    }

    if (driftDuration < 0.8) {
      return 'drift_ground_level';
    }

    if (intensity > 0.7 && driftDuration > 1.5 && this.driftShotCooldown <= 0) {
      const shots: ShotType[] = [
        'drift_orbit',
        'drift_low_sweep',
        'drift_crane_up',
        'drift_reverse_track',
        'drift_360_spin',
        'tire_orbit',
      ];
      this.driftShotIndex = (this.driftShotIndex + 1) % shots.length;
      this.driftShotCooldown = 3.0;
      return shots[this.driftShotIndex]!;
    }

    if (intensity > 0.5) {
      if (this.currentShot === 'drift_wide') {
        if (this.shotTimer > 1.2 && Math.random() < 0.3) {
          return 'drift_orbit';
        }
      }
      return 'drift_wide';
    }

    return 'drift_wide';
  }

  private getDriftShotMinHold(): number {
    switch (this.currentShot) {
      case 'drift_orbit':
        return 2.5;
      case 'drift_low_sweep':
        return 1.8;
      case 'drift_ground_level':
        return 1.2;
      case 'drift_crane_up':
        return 2.2;
      case 'drift_reverse_track':
        return 1.5;
      case 'tire_orbit':
        return 2.0;
      case 'drift_360_spin':
        return 2.8;
      case 'drift_wide':
        return 0.8;
      default:
        return 0.5;
    }
  }

  private pickAirShot(input: DirectorInput): ShotType {
    if (this.isAirCinematicShot() && this.shotTimer < this.getAirShotMinHold()) {
      return this.currentShot;
    }

    const airTime = this.shotTimer;
    const speed = input.speedKmh;

    if (airTime < 0.3 && speed > 80) {
      return 'air_bullet_time';
    }

    if (input.carVelocity.y < -2 && airTime > 0.5) {
      return 'air_ground_anticipation';
    }

    const shots: ShotType[] = ['air_hero', 'air_bullet_time'];
    this.airShotIndex = (this.airShotIndex + 1) % shots.length;
    return shots[this.airShotIndex]!;
  }

  private isAirCinematicShot(): boolean {
    return (
      this.currentShot === 'air_hero' ||
      this.currentShot === 'air_bullet_time' ||
      this.currentShot === 'air_ground_anticipation'
    );
  }

  private getAirShotMinHold(): number {
    switch (this.currentShot) {
      case 'air_bullet_time':
        return 1.8;
      case 'air_ground_anticipation':
        return 1.0;
      case 'air_hero':
        return 1.2;
      default:
        return 0.5;
    }
  }

  private pickNitroShot(input: DirectorInput): ShotType {
    if (this.isNitroCinematicShot() && this.shotTimer < 1.5) {
      return this.currentShot;
    }

    if (input.speedKmh > 160 && Math.random() < 0.4) {
      return 'nitro_tunnel_rush';
    }

    return 'nitro_low_angle';
  }

  private isNitroCinematicShot(): boolean {
    return (
      this.currentShot === 'nitro_low_angle' ||
      this.currentShot === 'nitro_tunnel_rush'
    );
  }

  private pickCopShot(input: DirectorInput): ShotType {
    this.copShotCooldown = 3;

    const r = Math.random();

    if (input.copProximity > 0.9) {
      if (r < 0.2) return 'cop_ground_level';
      if (r < 0.4) return 'cop_360_orbit';
      if (r < 0.55) return 'cop_dolly_zoom';
      if (r < 0.7) return 'cop_bumper_cam';
      if (r < 0.85) return 'cop_dramatic_pursuit';
      return 'cop_crane_sweep';
    }

    if (input.copProximity > 0.75) {
      if (r < 0.15) return 'cop_ground_level';
      if (r < 0.3) return 'cop_360_orbit';
      if (r < 0.45) return 'cop_crane_sweep';
      if (r < 0.6) return 'cop_helicopter';
      if (r < 0.75) return 'cop_chase_wide';
      if (r < 0.9) return 'cop_dramatic_pursuit';
      return 'cop_dolly_zoom';
    }

    if (input.copProximity > 0.6) {
      if (r < 0.2) return 'cop_chase_wide';
      if (r < 0.4) return 'cop_helicopter';
      if (r < 0.6) return 'cop_dramatic_pursuit';
      if (r < 0.8) return 'cop_crane_sweep';
      return 'cop_two_shot';
    }

    return 'cop_two_shot';
  }

  private isCopCinematicShot(): boolean {
    return (
      this.currentShot === 'cop_two_shot' ||
      this.currentShot === 'cop_helicopter' ||
      this.currentShot === 'cop_bumper_cam' ||
      this.currentShot === 'cop_chase_wide' ||
      this.currentShot === 'cop_dramatic_pursuit' ||
      this.currentShot === 'cop_ground_level' ||
      this.currentShot === 'cop_360_orbit' ||
      this.currentShot === 'cop_dolly_zoom' ||
      this.currentShot === 'cop_crane_sweep'
    );
  }

  private getCopShotMinHold(): number {
    switch (this.currentShot) {
      case 'cop_helicopter':
        return 2.5;
      case 'cop_bumper_cam':
        return 1.8;
      case 'cop_two_shot':
        return 1.2;
      case 'cop_chase_wide':
        return 2.2;
      case 'cop_dramatic_pursuit':
        return 2.8;
      case 'cop_ground_level':
        return 2.0;
      case 'cop_360_orbit':
        return 3.0;
      case 'cop_dolly_zoom':
        return 2.5;
      case 'cop_crane_sweep':
        return 2.8;
      default:
        return 1.0;
    }
  }

  private pickSpeedShot(input: DirectorInput): ShotType {
    this.speedShotCooldown = 8;

    if (input.speedKmh > 170) {
      const shots: ShotType[] = [
        'speed_flyby',
        'speed_worm_eye',
        'speed_rush',
        'speed_360_orbit',
      ];
      this.speedShotIndex = (this.speedShotIndex + 1) % shots.length;
      return shots[this.speedShotIndex]!;
    }

    return 'speed_rush';
  }

  private isSpeedCinematicShot(): boolean {
    return (
      this.currentShot === 'speed_rush' ||
      this.currentShot === 'speed_flyby' ||
      this.currentShot === 'speed_worm_eye' ||
      this.currentShot === 'speed_360_orbit'
    );
  }

  private getSpeedShotMinHold(): number {
    switch (this.currentShot) {
      case 'speed_flyby':
        return 2.0;
      case 'speed_worm_eye':
        return 1.5;
      case 'speed_rush':
        return 2.0;
      default:
        return 1.0;
    }
  }

  private onShotChange(newShot: ShotType, input: DirectorInput): void {
    this.currentShot = newShot;
    this.shotTimer = 0;
    this.lastShotChange = this.totalTime;

    const lat =
      input.carVelocity.x * Math.cos(input.carRotation) -
      input.carVelocity.z * Math.sin(input.carRotation);
    this.driftSide = lat > 0 ? -1 : 1;

    switch (newShot) {
      case 'impact_reaction':
        this.collisionSide = Math.random() < 0.5 ? 1 : -1;
        this.shotHoldTime = 0.35;
        break;
      case 'near_miss':
        this.shotHoldTime = 1.2;
        break;
      case 'drift_wide':
        this.shotHoldTime = 0.8;
        break;
      case 'drift_orbit':
        this.driftOrbitAngle = input.carRotation + Math.PI * 0.75 * this.driftSide;
        this.shotHoldTime = 2.5;
        break;
      case 'drift_low_sweep':
        this.shotHoldTime = 1.8;
        break;
      case 'drift_ground_level':
        this.shotHoldTime = 1.2;
        break;
      case 'drift_crane_up':
        this.shotHoldTime = 2.2;
        break;
      case 'drift_reverse_track':
        this.shotHoldTime = 1.5;
        break;
      case 'cop_two_shot':
        this.copShotCooldown = 8;
        this.shotHoldTime = 1.0;
        break;
      case 'cop_helicopter':
        this.shotHoldTime = 2.5;
        break;
      case 'cop_bumper_cam':
        this.shotHoldTime = 1.5;
        break;
      case 'speed_rush':
        this.speedRushCooldown = 12;
        this.shotHoldTime = 2.0;
        break;
      case 'speed_flyby':
        this.flybyStartPos.copy(input.carPosition);
        this.flybyDirection.copy(input.carVelocity).normalize();
        this.shotHoldTime = 2.0;
        break;
      case 'speed_worm_eye':
        this.shotHoldTime = 1.5;
        break;
      case 'hood_cam':
        this.hoodCamCooldown = 15;
        this.shotHoldTime = 1.5;
        break;
      case 'air_hero':
        this.shotHoldTime = 1.2;
        break;
      case 'air_bullet_time':
        this.shotHoldTime = 1.8;
        break;
      case 'air_ground_anticipation':
        this.shotHoldTime = 1.0;
        break;
      case 'nitro_low_angle':
        this.shotHoldTime = 1.5;
        break;
      case 'nitro_tunnel_rush':
        this.shotHoldTime = 2.0;
        break;
      default:
        this.shotHoldTime = 0.3;
    }
  }

  private computeShotCamera(input: DirectorInput): void {
    const { carPosition, carRotation, speedKmh, carVelocity } = input;
    const h = carRotation;

    const back = new THREE.Vector3(-Math.sin(h), 0, -Math.cos(h));
    const fwd = new THREE.Vector3(Math.sin(h), 0, Math.cos(h));
    const right = new THREE.Vector3(Math.cos(h), 0, -Math.sin(h));

    const speed01 = Math.min(1, speedKmh / 180);

    switch (this.currentShot) {
      case 'chase_tracking':
        this.shotChaseTracking(carPosition, back, fwd, speed01, carVelocity);
        break;
      case 'drift_wide':
        this.shotDriftWide(carPosition, back, fwd, right, input.driftAngle);
        break;
      case 'drift_orbit':
        this.shotDriftOrbit(carPosition, input.driftAngle, carVelocity);
        break;
      case 'drift_low_sweep':
        this.shotDriftLowSweep(carPosition, back, fwd, right, input.driftAngle);
        break;
      case 'drift_ground_level':
        this.shotDriftGroundLevel(carPosition, back, fwd, right, input.driftAngle);
        break;
      case 'drift_crane_up':
        this.shotDriftCraneUp(carPosition, input.driftAngle, carVelocity);
        break;
      case 'drift_reverse_track':
        this.shotDriftReverseTrack(carPosition, fwd, right, input.driftAngle);
        break;
      case 'drift_360_spin':
        this.shotDrift360Spin(carPosition, input.driftAngle, carVelocity);
        break;
      case 'nitro_low_angle':
        this.shotNitroLowAngle(carPosition, back, fwd, speed01);
        break;
      case 'air_hero':
        this.shotAirHero(carPosition, h, carVelocity);
        break;
      case 'air_bullet_time':
        this.shotAirBulletTime(carPosition, h, carVelocity);
        break;
      case 'air_ground_anticipation':
        this.shotAirGroundAnticipation(carPosition, carVelocity);
        break;
      case 'cop_two_shot':
        this.shotCopTwoShot(carPosition, back, fwd, right);
        break;
      case 'cop_helicopter':
        this.shotCopHelicopter(carPosition, carVelocity, input.helicopterPosition);
        break;
      case 'cop_bumper_cam':
        this.shotCopBumperCam(carPosition, back, fwd);
        break;
      case 'cop_chase_wide':
        this.shotCopChaseWide(carPosition, back, fwd, right, carVelocity);
        break;
      case 'cop_dramatic_pursuit':
        this.shotCopDramaticPursuit(carPosition, carVelocity, h);
        break;
      case 'cop_ground_level':
        this.shotCopGroundLevel(carPosition, back, fwd, right, carVelocity);
        break;
      case 'cop_360_orbit':
        this.shotCop360Orbit(carPosition, carVelocity, h);
        break;
      case 'cop_dolly_zoom':
        this.shotCopDollyZoom(carPosition, back, fwd, carVelocity);
        break;
      case 'cop_crane_sweep':
        this.shotCopCraneSweep(carPosition, carVelocity, h);
        break;
      case 'impact_reaction':
        this.shotImpactReaction(carPosition, right, input.collisionImpactForce);
        break;
      case 'near_miss':
        this.shotNearMiss(carPosition, back, fwd, right);
        break;
      case 'speed_rush':
        this.shotSpeedRush(carPosition, back, fwd, right, speed01);
        break;
      case 'speed_flyby':
        this.shotSpeedFlyby(carPosition, carVelocity);
        break;
      case 'speed_worm_eye':
        this.shotSpeedWormEye(carPosition, back, fwd, right);
        break;
      case 'speed_360_orbit':
        this.shotSpeed360Orbit(carPosition, carVelocity, h);
        break;
      case 'nitro_tunnel_rush':
        this.shotNitroTunnelRush(carPosition, back, fwd, speed01);
        break;
      case 'hood_cam':
        this.shotHoodCam(carPosition, fwd);
        break;
      case 'tire_orbit':
        this.shotTireOrbit(carPosition, right, h, carVelocity, input.driftAngle);
        break;
      case 'start_tire_reveal':
        this.shotStartTireReveal(carPosition, right, h, carVelocity, input.speedKmh);
        break;
      case 'helicopter_reveal':
        this.shotHelicopterReveal(carPosition, carVelocity, input.helicopterPosition);
        break;
      case 'helicopter_swoop':
        this.shotHelicopterSwoop(carPosition, carVelocity, input.helicopterPosition);
        break;
      case 'helicopter_chase':
        this.shotHelicopterChase(carPosition, carVelocity, input.helicopterPosition);
        break;
      case 'pursuit_breaker_cam':
        this.shotPursuitBreakerCam(carPosition, carVelocity, h);
        break;
      case 'victory_celebration':
        this.shotVictoryCelebration(carPosition, input.extractionDirection, h);
        break;
    }
  }

  private shotChaseTracking(
    pos: THREE.Vector3,
    back: THREE.Vector3,
    fwd: THREE.Vector3,
    speed: number,
    vel: THREE.Vector3,
  ): void {
    const dist = 8 + speed * 6;
    const height = 2.2 + speed * 2.5;
    const lookAhead = 4 + speed * 10;

    const lag = 0.2 + speed * 0.1;
    const lagOffset = new THREE.Vector3(vel.x * lag, 0, vel.z * lag);

    const sway = Math.sin(this.totalTime * 0.5) * 0.4;
    const breathe = Math.sin(this.totalTime * 0.8) * 0.25;

    this.targetPos.copy(pos)
      .addScaledVector(back, dist)
      .add(lagOffset)
      .setY(pos.y + height + breathe);
    this.targetPos.x += sway;

    this.targetLook.copy(pos)
      .addScaledVector(fwd, lookAhead)
      .setY(pos.y + 0.6);

    this.targetFov = 50 + speed * 25;

    const lateralVel = Math.abs(vel.x * Math.cos(Math.atan2(vel.x, vel.z)) - vel.z * Math.sin(Math.atan2(vel.x, vel.z)));
    this.targetDutch = THREE.MathUtils.clamp(lateralVel * 0.002, -0.06, 0.06);
  }

  private shotDriftWide(
    pos: THREE.Vector3,
    back: THREE.Vector3,
    fwd: THREE.Vector3,
    right: THREE.Vector3,
    driftAngle: number,
  ): void {
    const t = Math.min(1, this.shotTimer * 1.0);
    const ease = t * t * (3 - 2 * t);
    const intensity = Math.min(1, driftAngle / 45);

    const sideAmt = (10 + ease * 12 + intensity * 6) * this.driftSide;
    const backAmt = 4 - ease * 2;
    const height = 2.5 + ease * 4 + intensity * 2;

    const breathe = Math.sin(this.totalTime * 1.2) * 0.3;
    const sway = Math.sin(this.totalTime * 0.7) * 0.5;

    this.targetPos
      .copy(pos)
      .addScaledVector(right, sideAmt + sway)
      .addScaledVector(back, backAmt)
      .setY(pos.y + height + breathe);

    this.targetLook
      .copy(pos)
      .addScaledVector(fwd, 6 + intensity * 3)
      .setY(pos.y + 0.3);

    this.targetFov = 42 + ease * 28 + intensity * 12;

    this.targetDutch =
      -this.driftSide * (0.14 + ease * 0.18 + intensity * 0.1) +
      Math.sin(this.totalTime * 1.5) * 0.03;
  }

  private shotDriftOrbit(
    pos: THREE.Vector3,
    driftAngle: number,
    velocity: THREE.Vector3,
  ): void {
    const t = this.shotTimer;
    const intensity = Math.min(1, driftAngle / 50);

    const orbitSpeed = 0.8 + intensity * 0.4;
    this.driftOrbitAngle += orbitSpeed * 0.016;

    const orbitRadius = 12 + Math.sin(t * 0.5) * 3 - intensity * 2;
    const orbitHeight = 3 + Math.sin(t * 0.8) * 1.5 + intensity * 2;

    const velOffset = velocity.clone().multiplyScalar(0.15);

    this.targetPos.set(
      pos.x + Math.cos(this.driftOrbitAngle) * orbitRadius + velOffset.x,
      pos.y + orbitHeight,
      pos.z + Math.sin(this.driftOrbitAngle) * orbitRadius + velOffset.z,
    );

    const lookAhead = velocity.clone().normalize().multiplyScalar(3);
    this.targetLook
      .copy(pos)
      .add(lookAhead)
      .setY(pos.y + 0.5);

    this.targetFov = 38 + intensity * 15 + Math.sin(t * 1.2) * 5;

    const orbitPhase = Math.sin(this.driftOrbitAngle * 2);
    this.targetDutch = orbitPhase * 0.15 + intensity * 0.08 * this.driftSide;
  }

  private shotDriftLowSweep(
    pos: THREE.Vector3,
    back: THREE.Vector3,
    fwd: THREE.Vector3,
    right: THREE.Vector3,
    driftAngle: number,
  ): void {
    const t = this.shotTimer;
    const sweepPhase = Math.sin(t * Math.PI * 0.4);
    const intensity = Math.min(1, driftAngle / 45);

    const sideAmt = sweepPhase * 14 * this.driftSide;
    const backAmt = 6 - Math.abs(sweepPhase) * 3;
    const height = 0.8 + Math.abs(sweepPhase) * 0.6;

    const groundHug = Math.sin(this.totalTime * 3) * 0.08;

    this.targetPos
      .copy(pos)
      .addScaledVector(right, sideAmt)
      .addScaledVector(back, backAmt)
      .setY(pos.y + height + groundHug);

    this.targetLook
      .copy(pos)
      .addScaledVector(fwd, 4)
      .setY(pos.y + 0.8);

    this.targetFov = 55 + Math.abs(sweepPhase) * 25 + intensity * 10;

    this.targetDutch =
      -sweepPhase * 0.22 + Math.sin(this.totalTime * 2) * 0.04;
  }

  private shotDriftGroundLevel(
    pos: THREE.Vector3,
    back: THREE.Vector3,
    fwd: THREE.Vector3,
    right: THREE.Vector3,
    driftAngle: number,
  ): void {
    const t = Math.min(1, this.shotTimer * 0.8);
    const ease = t * t * (3 - 2 * t);
    const intensity = Math.min(1, driftAngle / 40);

    const sideAmt = (5 + ease * 8) * this.driftSide;
    const backAmt = 3 + ease * 2;
    const height = 0.4 + ease * 0.3;

    const roadVibration =
      Math.sin(this.totalTime * 25) * 0.02 +
      Math.sin(this.totalTime * 17) * 0.015;

    this.targetPos
      .copy(pos)
      .addScaledVector(right, sideAmt)
      .addScaledVector(back, backAmt)
      .setY(pos.y + height + roadVibration);

    const tireFocus = right.clone().multiplyScalar(1.2 * this.driftSide);
    this.targetLook
      .copy(pos)
      .add(tireFocus)
      .addScaledVector(fwd, 2)
      .setY(pos.y + 0.3);

    this.targetFov = 65 + ease * 20 + intensity * 15;

    this.targetDutch =
      -this.driftSide * (0.18 + ease * 0.12) +
      Math.sin(this.totalTime * 4) * 0.02;
  }

  private shotDriftCraneUp(
    pos: THREE.Vector3,
    driftAngle: number,
    velocity: THREE.Vector3,
  ): void {
    const t = this.shotTimer;
    const cranePhase = Math.min(1, t / 2.5);
    const ease = cranePhase * cranePhase * (3 - 2 * cranePhase);
    const intensity = Math.min(1, driftAngle / 45);

    const startHeight = 1.5;
    const endHeight = 18 + intensity * 8;
    const height = startHeight + ease * (endHeight - startHeight);

    const startDist = 8;
    const endDist = 25 + intensity * 10;
    const dist = startDist + ease * (endDist - startDist);

    const velDir = velocity.clone().normalize();
    const backDir = velDir.clone().multiplyScalar(-1);

    const spiralAngle = t * 0.3;
    const spiralOffset = new THREE.Vector3(
      Math.cos(spiralAngle) * 3,
      0,
      Math.sin(spiralAngle) * 3,
    );

    this.targetPos
      .copy(pos)
      .addScaledVector(backDir, dist)
      .add(spiralOffset)
      .setY(pos.y + height);

    this.targetLook.copy(pos).setY(pos.y + 0.5 + ease * 2);

    const startFov = 70;
    const endFov = 35;
    this.targetFov = startFov + ease * (endFov - startFov) + intensity * 8;

    this.targetDutch =
      Math.sin(t * 0.8) * 0.1 * (1 - ease) +
      this.driftSide * 0.05 * intensity;
  }

  private shotDriftReverseTrack(
    pos: THREE.Vector3,
    fwd: THREE.Vector3,
    right: THREE.Vector3,
    driftAngle: number,
  ): void {
    const t = this.shotTimer;
    const intensity = Math.min(1, driftAngle / 45);

    const frontDist = 8 + Math.sin(t * 0.6) * 2;
    const sideAmt = (3 + intensity * 4) * this.driftSide;
    const height = 1.2 + intensity * 0.8;

    const trackSway = Math.sin(t * 1.5) * 1.5;

    this.targetPos
      .copy(pos)
      .addScaledVector(fwd, frontDist)
      .addScaledVector(right, sideAmt + trackSway)
      .setY(pos.y + height);

    this.targetLook.copy(pos).setY(pos.y + 0.4);

    this.targetFov = 50 + intensity * 18 + Math.sin(t * 2) * 5;

    this.targetDutch =
      this.driftSide * (0.1 + intensity * 0.12) +
      Math.sin(t * 2.5) * 0.05;
  }

  private shotDrift360Spin(
    pos: THREE.Vector3,
    driftAngle: number,
    vel: THREE.Vector3,
  ): void {
    const t = this.shotTimer;
    const intensity = Math.min(1, driftAngle / 50);

    const spinSpeed = 2.5 + intensity * 1.5;
    const spinAngle = t * spinSpeed;

    const dist = 10 + Math.sin(t * 0.8) * 3;
    const height = 3 + Math.sin(t * 1.2) * 1.5 + intensity * 2;

    const velOffset = vel.clone().multiplyScalar(0.2);

    this.targetPos.set(
      pos.x + Math.cos(spinAngle) * dist + velOffset.x,
      pos.y + height,
      pos.z + Math.sin(spinAngle) * dist + velOffset.z,
    );

    this.targetLook.copy(pos).setY(pos.y + 0.5);

    this.targetFov = 40 + intensity * 20 + Math.sin(t * 2) * 8;

    this.targetDutch = Math.sin(spinAngle * 0.5) * 0.2 + intensity * 0.1 * this.driftSide;
  }

  /** Wide sweeping shot - dramatic side-to-side movement */
  private shotCopChaseWide(
    pos: THREE.Vector3,
    back: THREE.Vector3,
    fwd: THREE.Vector3,
    right: THREE.Vector3,
    vel: THREE.Vector3,
  ): void {
    const t = this.shotTimer;
    const sweepPhase = Math.sin(t * Math.PI * 0.35);
    const speed = vel.length();
    const intensity = Math.min(1, speed / 18);

    const sideAmt = sweepPhase * 18 * this.driftSide;
    const backAmt = 7 - Math.abs(sweepPhase) * 3;
    const height = 3.5 + Math.abs(sweepPhase) * 2 + intensity * 1.5;

    const velLag = vel.clone().multiplyScalar(0.2);

    this.targetPos
      .copy(pos)
      .addScaledVector(right, sideAmt)
      .addScaledVector(back, backAmt)
      .add(velLag)
      .setY(pos.y + height);

    const lookAhead = fwd.clone().multiplyScalar(6 + intensity * 4);
    this.targetLook
      .copy(pos)
      .add(lookAhead)
      .setY(pos.y + 0.6);

    this.targetFov = 52 + Math.abs(sweepPhase) * 22 + intensity * 8;

    this.targetDutch =
      -sweepPhase * 0.18 + Math.sin(this.totalTime * 2.5) * 0.05;
  }

  /** Dramatic pursuit - intense orbiting chase shot */
  private shotCopDramaticPursuit(
    pos: THREE.Vector3,
    vel: THREE.Vector3,
    heading: number,
  ): void {
    const t = this.shotTimer;
    const speed = vel.length();
    const intensity = Math.min(1, speed / 16);

    const orbitSpeed = 0.7 + intensity * 0.4;
    const orbit = heading + Math.PI * 0.75 + t * orbitSpeed;

    const dist = 10 + Math.sin(t * 0.6) * 4 - intensity * 2;
    const height = 2 + Math.sin(t * 0.9) * 1.2 + intensity * 1.5;

    const velOffset = vel.clone().multiplyScalar(0.25);

    const breathe = Math.sin(this.totalTime * 3) * 0.1;

    this.targetPos.set(
      pos.x + Math.cos(orbit) * dist + velOffset.x,
      pos.y + height + breathe,
      pos.z + Math.sin(orbit) * dist + velOffset.z,
    );

    const lookAhead = vel.clone().normalize().multiplyScalar(5 + intensity * 3);
    this.targetLook.copy(pos).add(lookAhead).setY(pos.y + 0.7);

    this.targetFov = 40 + intensity * 15 + Math.sin(t * 1.8) * 8;

    const orbitPhase = Math.sin(orbit * 1.5);
    this.targetDutch = orbitPhase * 0.18 + intensity * 0.08 * this.driftSide;
  }

  private shotSpeed360Orbit(
    pos: THREE.Vector3,
    vel: THREE.Vector3,
    heading: number,
  ): void {
    const t = this.shotTimer;

    const orbitSpeed = 3.0;
    const orbit = heading + t * orbitSpeed;

    const dist = 8 + Math.sin(t * 1.5) * 2;
    const height = 2 + Math.sin(t * 2) * 0.8;

    const velOffset = vel.clone().multiplyScalar(0.15);

    this.targetPos.set(
      pos.x + Math.cos(orbit) * dist + velOffset.x,
      pos.y + height,
      pos.z + Math.sin(orbit) * dist + velOffset.z,
    );

    this.targetLook.copy(pos).setY(pos.y + 0.5);

    this.targetFov = 50 + Math.sin(t * 3) * 15;

    this.targetDutch = Math.sin(orbit * 0.5) * 0.18;
  }

  private shotNitroLowAngle(
    pos: THREE.Vector3,
    back: THREE.Vector3,
    fwd: THREE.Vector3,
    speed: number,
  ): void {
    const t = Math.min(1, this.shotTimer * 1.0);
    const punch = Math.sin(t * Math.PI * 0.5);

    const dist = 5 - punch * 2;
    const height = 0.9 + punch * 0.3;
    const lookAhead = 15 + punch * 10;

    const breathe = Math.sin(this.totalTime * 4) * 0.1;

    this.targetPos.copy(pos)
      .addScaledVector(back, dist)
      .setY(pos.y + height + breathe);

    this.targetLook.copy(pos)
      .addScaledVector(fwd, lookAhead)
      .setY(pos.y + 2);

    this.targetFov = 65 + punch * 40 + speed * 10;

    this.targetDutch = Math.sin(this.totalTime * 2.5) * 0.04;
  }

  private shotAirHero(pos: THREE.Vector3, heading: number, vel: THREE.Vector3): void {
    const t = this.shotTimer;
    const orbit = heading + Math.PI + t * 1.0;

    const verticalVel = vel.y;
    const phase = verticalVel > 0 ? 'rising' : 'falling';

    let dist: number;
    let height: number;

    if (phase === 'rising') {
      dist = 10 + t * 4;
      height = 4 + t * 5;
    } else {
      dist = 14 - t * 2;
      height = 8 - t * 2;
    }

    this.targetPos.set(
      pos.x - Math.sin(orbit) * dist,
      Math.max(pos.y + height, 3),
      pos.z - Math.cos(orbit) * dist,
    );

    this.targetLook.copy(pos).setY(pos.y + 0.3);

    this.targetFov = 42 + Math.sin(t * 2) * 15;

    this.targetDutch = Math.sin(t * 1.8) * 0.18;
  }

  private shotCopTwoShot(
    pos: THREE.Vector3,
    back: THREE.Vector3,
    fwd: THREE.Vector3,
    right: THREE.Vector3,
  ): void {
    const t = Math.min(1, this.shotTimer * 0.8);
    const ease = t * t * (3 - 2 * t);

    const sideAmt = 6 + ease * 3;
    const backAmt = 2 + ease * 2;
    const height = 2 + ease;

    this.targetPos.copy(pos)
      .addScaledVector(right, sideAmt)
      .addScaledVector(back, backAmt)
      .setY(pos.y + height);

    this.targetLook.copy(pos)
      .addScaledVector(fwd, 4)
      .setY(pos.y + 0.4);

    this.targetFov = 45 + ease * 15;

    this.targetDutch = -0.1 - ease * 0.08;
  }

  private shotImpactReaction(pos: THREE.Vector3, right: THREE.Vector3, force: number): void {
    const t = Math.min(1, this.shotTimer * 3);

    const sideAmt = (4 + force * 2) * this.collisionSide;
    const height = 1.8 + force;

    this.targetPos.copy(pos)
      .addScaledVector(right, sideAmt)
      .setY(pos.y + height);

    this.targetLook.copy(pos).setY(pos.y + 0.2);

    this.targetFov = 38 + t * 12;

    this.targetDutch = this.collisionSide * (0.2 - t * 0.15) * force;
  }

  private shotSpeedRush(
    pos: THREE.Vector3,
    back: THREE.Vector3,
    fwd: THREE.Vector3,
    right: THREE.Vector3,
    _speed: number,
  ): void {
    const t = this.shotTimer;
    const phase = t < 0.5 ? 'zoom_in' : t < 1.5 ? 'side_track' : 'zoom_out';

    if (phase === 'zoom_in') {
      const p = t * 2;
      const dist = 12 - p * 8;
      const height = 3 - p * 1.5;
      this.targetPos.copy(pos).addScaledVector(back, dist).setY(pos.y + height);
      this.targetFov = 50 + p * 35;
    } else if (phase === 'side_track') {
      const p = (t - 0.5);
      const sideAmt = Math.sin(p * Math.PI) * 8;
      this.targetPos.copy(pos)
        .addScaledVector(right, sideAmt)
        .addScaledVector(back, 4)
        .setY(pos.y + 1.5);
      this.targetFov = 85;
    } else {
      const p = (t - 1.5) * 2;
      const dist = 4 + p * 8;
      const height = 1.5 + p * 1.5;
      this.targetPos.copy(pos).addScaledVector(back, dist).setY(pos.y + height);
      this.targetFov = 85 - p * 35;
    }

    this.targetLook.copy(pos).addScaledVector(fwd, 8).setY(pos.y + 0.5);
    this.targetDutch = Math.sin(t * 3) * 0.1;
  }

  private shotHoodCam(pos: THREE.Vector3, fwd: THREE.Vector3): void {
    const breathe = Math.sin(this.totalTime * 3) * 0.05;

    this.targetPos.copy(pos)
      .addScaledVector(fwd, 1.2)
      .setY(pos.y + 0.8 + breathe);

    this.targetLook.copy(pos)
      .addScaledVector(fwd, 50)
      .setY(pos.y + 1);

    this.targetFov = 75 + Math.sin(this.totalTime * 2) * 5;

    this.targetDutch = Math.sin(this.totalTime * 1.5) * 0.03;
  }

  private shotAirBulletTime(
    pos: THREE.Vector3,
    heading: number,
    vel: THREE.Vector3,
  ): void {
    const t = this.shotTimer;

    const orbitSpeed = 2.5;
    const orbit = heading + Math.PI * 0.5 + t * orbitSpeed;

    const dist = 8 + Math.sin(t * 1.5) * 2;
    const height = 2 + vel.y * 0.3;

    this.targetPos.set(
      pos.x + Math.cos(orbit) * dist,
      Math.max(pos.y + height, 2),
      pos.z + Math.sin(orbit) * dist,
    );

    this.targetLook.copy(pos).setY(pos.y + 0.5);

    this.targetFov = 32 + Math.sin(t * 2) * 8;

    this.targetDutch = Math.sin(t * 1.2) * 0.25 + Math.cos(orbit) * 0.1;
  }

  private shotAirGroundAnticipation(
    pos: THREE.Vector3,
    vel: THREE.Vector3,
  ): void {
    const t = this.shotTimer;

    const landingPoint = pos.clone().add(vel.clone().multiplyScalar(0.5));
    landingPoint.y = 0;

    const camOffset = vel.clone().normalize().multiplyScalar(-12);
    camOffset.y = 0;

    this.targetPos
      .copy(landingPoint)
      .add(camOffset)
      .setY(1.5 + Math.sin(t * 3) * 0.2);

    this.targetLook.copy(pos).setY(pos.y - 1);

    this.targetFov = 55 + t * 15;

    this.targetDutch = Math.sin(t * 2) * 0.08;
  }

  private shotCopHelicopter(pos: THREE.Vector3, vel: THREE.Vector3, heliPos?: THREE.Vector3 | null): void {
    const t = this.shotTimer;

    if (heliPos) {
      // Use real helicopter position - camera near helicopter looking at player
      const heliToCar = pos.clone().sub(heliPos).normalize();
      const camOffset = heliToCar.clone().multiplyScalar(-8); // Behind helicopter
      camOffset.y = 5; // Slightly above
      
      this.targetPos.copy(heliPos).add(camOffset);
      this.targetLook.copy(pos).setY(pos.y + 1);
      
      // Wider FOV to show both helicopter and car
      this.targetFov = 55 + Math.sin(t * 0.5) * 5;
      this.targetDutch = Math.sin(t * 0.8) * 0.04;
    } else {
      // Fallback: simulate helicopter view
      const orbit = t * 0.4;
      const dist = 35 + Math.sin(t * 0.3) * 8;
      const height = 25 + Math.sin(t * 0.5) * 5;

      const velOffset = vel.clone().multiplyScalar(0.3);

      this.targetPos.set(
        pos.x + Math.cos(orbit) * dist + velOffset.x,
        pos.y + height,
        pos.z + Math.sin(orbit) * dist + velOffset.z,
      );

      this.targetLook.copy(pos).setY(pos.y + 1);
      this.targetFov = 28 + Math.sin(t * 0.8) * 5;
      this.targetDutch = Math.sin(t * 0.6) * 0.06;
    }
  }

  /**
   * Helicopter reveal shot - camera positioned to always show helicopter above car
   * Low angle looking up to capture both car and helicopter dramatically
   */
  private shotHelicopterReveal(pos: THREE.Vector3, _vel: THREE.Vector3, heliPos?: THREE.Vector3 | null): void {
    const t = this.shotTimer;
    
    if (heliPos) {
      // Camera positioned low and behind car, looking up to show helicopter
      const carToHeli = heliPos.clone().sub(pos);
      const heliDir = carToHeli.clone().setY(0).normalize();
      
      // Position camera on opposite side of car from helicopter (so heli is in frame above car)
      const camDist = 18 + Math.sin(t * 0.3) * 3;
      const camHeight = 3 + Math.sin(t * 0.5) * 1; // Low angle
      
      // Camera behind and to the side, opposite the helicopter
      this.targetPos.set(
        pos.x - heliDir.x * camDist + Math.sin(t * 0.4) * 5,
        pos.y + camHeight,
        pos.z - heliDir.z * camDist + Math.cos(t * 0.4) * 5,
      );
      
      // Look at a point between car and helicopter (weighted toward helicopter)
      const lookTarget = pos.clone().lerp(heliPos, 0.4);
      lookTarget.y = pos.y + 8; // Look upward to include helicopter
      this.targetLook.copy(lookTarget);
      
      // Wide FOV to capture both subjects
      this.targetFov = 70 + Math.sin(t * 0.5) * 5;
      this.targetDutch = Math.sin(t * 0.7) * 0.04;
    } else {
      // Fallback: dramatic high angle
      const orbit = t * 0.5;
      const dist = 25;
      const height = 15;
      
      this.targetPos.set(
        pos.x + Math.cos(orbit) * dist,
        pos.y + height,
        pos.z + Math.sin(orbit) * dist,
      );
      
      this.targetLook.copy(pos).setY(pos.y + 5);
      this.targetFov = 60;
      this.targetDutch = Math.sin(t * 0.6) * 0.04;
    }
  }

  /**
   * Helicopter swoop shot - helicopter swoops in front of the car
   * Camera is positioned to see helicopter crossing in front of player
   */
  private shotHelicopterSwoop(pos: THREE.Vector3, vel: THREE.Vector3, heliPos?: THREE.Vector3 | null): void {
    const t = this.shotTimer;
    
    if (heliPos) {
      // Camera positioned to the side of the car, looking forward
      // This captures the helicopter swooping across the car's path
      const velDir = vel.clone().setY(0).normalize();
      const sideDir = new THREE.Vector3(-velDir.z, 0, velDir.x);
      
      // Camera sweeps from one side to the other
      const sweepPhase = Math.sin(t * 0.8);
      const camDist = 12 + Math.abs(sweepPhase) * 5;
      const camHeight = 2 + t * 0.5;
      
      this.targetPos.set(
        pos.x + sideDir.x * camDist * sweepPhase,
        pos.y + camHeight,
        pos.z + sideDir.z * camDist * sweepPhase,
      );
      
      // Look ahead of the car where helicopter will cross
      const lookAhead = pos.clone().addScaledVector(velDir, 15 + t * 5);
      lookAhead.y = pos.y + 10; // Look up slightly to catch helicopter
      this.targetLook.copy(lookAhead);
      
      // Dynamic FOV - wider as helicopter crosses
      this.targetFov = 55 + Math.sin(t * 1.5) * 15;
      this.targetDutch = sweepPhase * 0.08;
    } else {
      // Fallback
      this.shotHelicopterReveal(pos, vel, heliPos);
    }
  }

  /**
   * Helicopter chase shot - camera behind car looking up at pursuing helicopter
   * Creates intense "being hunted" feeling
   */
  private shotHelicopterChase(pos: THREE.Vector3, vel: THREE.Vector3, heliPos?: THREE.Vector3 | null): void {
    const t = this.shotTimer;
    
    if (heliPos) {
      // Camera low and behind the car, looking up and back at helicopter
      const velDir = vel.clone().setY(0);
      const speed = velDir.length();
      if (speed > 0.1) velDir.normalize();
      else velDir.set(0, 0, -1);
      
      // Camera trails behind the car
      const trailDist = 8 + Math.sin(t * 0.5) * 2;
      const camHeight = 1.5 + Math.sin(t * 0.7) * 0.5; // Very low
      
      // Add some shake for intensity
      const shake = Math.sin(t * 15) * 0.1 + Math.sin(t * 23) * 0.05;
      
      this.targetPos.set(
        pos.x - velDir.x * trailDist + shake,
        pos.y + camHeight,
        pos.z - velDir.z * trailDist + shake,
      );
      
      // Look at a point between car roof and helicopter - dramatic upward angle
      const lookTarget = pos.clone();
      lookTarget.y = pos.y + 3; // Car roof level
      
      // Blend toward helicopter position for dramatic reveal
      const heliBlend = Math.min(1, t * 0.4);
      lookTarget.lerp(heliPos, heliBlend * 0.5);
      
      this.targetLook.copy(lookTarget);
      
      // Tight FOV for intensity, opens up as we reveal helicopter
      this.targetFov = 45 + heliBlend * 20;
      this.targetDutch = Math.sin(t * 2) * 0.06;
    } else {
      // Fallback
      this.shotHelicopterReveal(pos, vel, heliPos);
    }
  }

  private shotCopBumperCam(
    pos: THREE.Vector3,
    back: THREE.Vector3,
    fwd: THREE.Vector3,
  ): void {
    const t = this.shotTimer;

    const dist = 3 + Math.sin(t * 2) * 0.5;
    const height = 0.6;

    const shake = Math.sin(t * 15) * 0.03 + Math.sin(t * 23) * 0.02;

    this.targetPos
      .copy(pos)
      .addScaledVector(back, dist)
      .setY(pos.y + height + shake);

    this.targetLook
      .copy(pos)
      .addScaledVector(fwd, 8)
      .setY(pos.y + 0.8);

    this.targetFov = 70 + Math.sin(t * 1.5) * 8;

    this.targetDutch = Math.sin(t * 4) * 0.04;
  }

  /** 
   * Ground-level dramatic shot - EPIC like drift_ground_level
   * Camera scrapes the road, looking up at the car with road vibration
   */
  private shotCopGroundLevel(
    pos: THREE.Vector3,
    back: THREE.Vector3,
    fwd: THREE.Vector3,
    right: THREE.Vector3,
    vel: THREE.Vector3,
  ): void {
    const t = Math.min(1, this.shotTimer * 0.7);
    const ease = t * t * (3 - 2 * t);
    const speed = vel.length();
    const intensity = Math.min(1, speed / 20);

    const sideAmt = (6 + ease * 10) * this.driftSide;
    const backAmt = 4 + ease * 3;
    const height = 0.35 + ease * 0.25;

    const roadVibration =
      Math.sin(this.totalTime * 28) * 0.025 +
      Math.sin(this.totalTime * 19) * 0.018 +
      Math.sin(this.totalTime * 37) * 0.01;

    const velLag = vel.clone().multiplyScalar(0.12);

    this.targetPos
      .copy(pos)
      .addScaledVector(right, sideAmt)
      .addScaledVector(back, backAmt)
      .add(velLag)
      .setY(height + roadVibration);

    const tireFocus = right.clone().multiplyScalar(1.5 * this.driftSide);
    this.targetLook
      .copy(pos)
      .add(tireFocus)
      .addScaledVector(fwd, 3)
      .setY(pos.y + 0.4);

    this.targetFov = 72 + ease * 25 + intensity * 12;

    this.targetDutch =
      -this.driftSide * (0.2 + ease * 0.15) +
      Math.sin(this.totalTime * 5) * 0.03;
  }

  /** 
   * Epic 360 orbit - DRAMATIC spinning shot like drift_orbit
   * Camera spirals around with intensity-based speed
   */
  private shotCop360Orbit(
    pos: THREE.Vector3,
    vel: THREE.Vector3,
    _heading: number,
  ): void {
    const t = this.shotTimer;
    const speed = vel.length();
    const intensity = Math.min(1, speed / 18);

    const orbitSpeed = 1.2 + intensity * 0.8;
    this.driftOrbitAngle += orbitSpeed * 0.016;

    const orbitRadius = 10 + Math.sin(t * 0.6) * 3 - intensity * 2;
    const orbitHeight = 2.5 + Math.sin(t * 0.9) * 1.8 + intensity * 1.5;

    const velOffset = vel.clone().multiplyScalar(0.18);

    this.targetPos.set(
      pos.x + Math.cos(this.driftOrbitAngle) * orbitRadius + velOffset.x,
      pos.y + orbitHeight,
      pos.z + Math.sin(this.driftOrbitAngle) * orbitRadius + velOffset.z,
    );

    const lookAhead = vel.clone().normalize().multiplyScalar(4);
    this.targetLook
      .copy(pos)
      .add(lookAhead)
      .setY(pos.y + 0.6);

    this.targetFov = 42 + intensity * 18 + Math.sin(t * 1.5) * 8;

    const orbitPhase = Math.sin(this.driftOrbitAngle * 2);
    this.targetDutch = orbitPhase * 0.2 + intensity * 0.1 * this.driftSide;
  }

  /** 
   * Dolly zoom (Vertigo effect) - INTENSE zoom with smooth easing
   * Creates the iconic "world closing in" feeling
   */
  private shotCopDollyZoom(
    pos: THREE.Vector3,
    back: THREE.Vector3,
    fwd: THREE.Vector3,
    vel: THREE.Vector3,
  ): void {
    const t = this.shotTimer;
    const phase = Math.min(1, t / 2.2);
    const ease = phase * phase * (3 - 2 * phase);
    const speed = vel.length();
    const intensity = Math.min(1, speed / 15);

    const startDist = 5;
    const endDist = 22;
    const dist = startDist + (endDist - startDist) * ease;

    const startFov = 75;
    const endFov = 22;
    const fov = startFov + (endFov - startFov) * ease;

    const height = 1.8 + ease * 2.5;

    const breathe = Math.sin(this.totalTime * 2.5) * 0.15;
    const velLag = vel.clone().multiplyScalar(0.1);

    this.targetPos
      .copy(pos)
      .addScaledVector(back, dist)
      .add(velLag)
      .setY(pos.y + height + breathe);

    const lookAhead = fwd.clone().multiplyScalar(2 + ease * 3);
    this.targetLook
      .copy(pos)
      .add(lookAhead)
      .setY(pos.y + 0.6 + ease * 0.4);

    this.targetFov = fov + intensity * 5;

    this.targetDutch = 
      Math.sin(t * 0.6) * 0.1 * (1 - ease * 0.5) +
      this.driftSide * 0.04 * intensity;
  }

  /** 
   * Crane sweep - EPIC vertical movement like drift_crane_up
   * Dramatic rise with spiral and smooth easing
   */
  private shotCopCraneSweep(
    pos: THREE.Vector3,
    vel: THREE.Vector3,
    _heading: number,
  ): void {
    const t = this.shotTimer;
    const cranePhase = Math.min(1, t / 2.8);
    const ease = cranePhase * cranePhase * (3 - 2 * cranePhase);
    const speed = vel.length();
    const intensity = Math.min(1, speed / 18);

    const startHeight = 1.2;
    const peakHeight = 22 + intensity * 8;
    const height = startHeight + ease * (peakHeight - startHeight);

    const startDist = 6;
    const endDist = 28 + intensity * 12;
    const dist = startDist + ease * (endDist - startDist);

    const velDir = vel.clone().normalize();
    const backDir = velDir.clone().multiplyScalar(-1);

    const spiralAngle = t * 0.4;
    const spiralRadius = 4 * (1 - ease * 0.5);
    const spiralOffset = new THREE.Vector3(
      Math.cos(spiralAngle) * spiralRadius,
      0,
      Math.sin(spiralAngle) * spiralRadius,
    );

    this.targetPos
      .copy(pos)
      .addScaledVector(backDir, dist)
      .add(spiralOffset)
      .setY(pos.y + height);

    this.targetLook.copy(pos).setY(pos.y + 0.5 + ease * 2.5);

    const startFov = 72;
    const endFov = 32;
    this.targetFov = startFov + ease * (endFov - startFov) + intensity * 10;

    this.targetDutch =
      Math.sin(t * 0.9) * 0.12 * (1 - ease * 0.6) +
      this.driftSide * 0.06 * intensity;
  }

  private shotNearMiss(
    pos: THREE.Vector3,
    back: THREE.Vector3,
    fwd: THREE.Vector3,
    right: THREE.Vector3,
  ): void {
    const t = this.shotTimer;
    const phase = t < 0.4 ? 'freeze' : 'pull_back';

    if (phase === 'freeze') {
      const sideAmt = 3 * this.collisionSide;
      this.targetPos
        .copy(pos)
        .addScaledVector(right, sideAmt)
        .addScaledVector(back, 2)
        .setY(pos.y + 1.2);

      this.targetLook.copy(pos).setY(pos.y + 0.3);

      this.targetFov = 35;
      this.targetDutch = this.collisionSide * 0.2;
    } else {
      const p = (t - 0.4) / 0.8;
      const ease = p * p * (3 - 2 * p);

      const sideAmt = (3 + ease * 6) * this.collisionSide;
      const backAmt = 2 + ease * 8;
      const height = 1.2 + ease * 3;

      this.targetPos
        .copy(pos)
        .addScaledVector(right, sideAmt)
        .addScaledVector(back, backAmt)
        .setY(pos.y + height);

      this.targetLook
        .copy(pos)
        .addScaledVector(fwd, 5)
        .setY(pos.y + 0.5);

      this.targetFov = 35 + ease * 30;
      this.targetDutch = this.collisionSide * (0.2 - ease * 0.15);
    }
  }

  private shotSpeedFlyby(pos: THREE.Vector3, vel: THREE.Vector3): void {
    const t = this.shotTimer;
    const duration = 2.0;
    const phase = Math.min(1, t / duration);

    const velDir = vel.clone().normalize();
    const perpDir = new THREE.Vector3(-velDir.z, 0, velDir.x);

    const startOffset = velDir.clone().multiplyScalar(25);
    const endOffset = velDir.clone().multiplyScalar(-20);

    const camPath = startOffset.lerp(endOffset, phase);

    const sideOffset = Math.sin(phase * Math.PI) * 8;
    const heightCurve = Math.sin(phase * Math.PI) * 3 + 1.5;

    this.targetPos
      .copy(this.flybyStartPos)
      .add(camPath)
      .addScaledVector(perpDir, sideOffset)
      .setY(this.flybyStartPos.y + heightCurve);

    this.targetLook.copy(pos).setY(pos.y + 0.5);

    const fovCurve = Math.sin(phase * Math.PI);
    this.targetFov = 45 + fovCurve * 40;

    this.targetDutch = Math.sin(phase * Math.PI * 2) * 0.12;
  }

  private shotSpeedWormEye(
    pos: THREE.Vector3,
    _back: THREE.Vector3,
    fwd: THREE.Vector3,
    right: THREE.Vector3,
  ): void {
    const t = this.shotTimer;

    const sideAmt = Math.sin(t * 1.2) * 4;
    const frontAmt = 6 + Math.sin(t * 0.8) * 2;

    const groundShake =
      Math.sin(t * 20) * 0.015 + Math.sin(t * 31) * 0.01;

    this.targetPos
      .copy(pos)
      .addScaledVector(fwd, frontAmt)
      .addScaledVector(right, sideAmt)
      .setY(0.25 + groundShake);

    this.targetLook.copy(pos).setY(pos.y + 0.8);

    this.targetFov = 85 + Math.sin(t * 1.5) * 10;

    this.targetDutch = Math.sin(t * 2) * 0.15;
  }

  private shotNitroTunnelRush(
    pos: THREE.Vector3,
    back: THREE.Vector3,
    fwd: THREE.Vector3,
    speed: number,
  ): void {
    const t = this.shotTimer;

    const dist = 4 + Math.sin(t * 3) * 1;
    const height = 0.7 + Math.sin(t * 2) * 0.15;

    const tunnel = Math.sin(t * 8) * 0.3;

    this.targetPos
      .copy(pos)
      .addScaledVector(back, dist)
      .setY(pos.y + height);
    this.targetPos.x += tunnel;

    this.targetLook
      .copy(pos)
      .addScaledVector(fwd, 20 + speed * 15)
      .setY(pos.y + 1.5);

    this.targetFov = 90 + Math.sin(t * 4) * 15 + speed * 20;

    this.targetDutch = Math.sin(t * 5) * 0.08;
  }

  private getShotSmoothing(): { pos: number; look: number; fov: number } {
    switch (this.currentShot) {
      case 'chase_tracking':
        return { pos: 5, look: 7, fov: 6 };
      case 'drift_wide':
        return { pos: 3.5, look: 5, fov: 4 };
      case 'drift_orbit':
        return { pos: 2.5, look: 4, fov: 3 };
      case 'drift_low_sweep':
        return { pos: 4, look: 6, fov: 5 };
      case 'drift_ground_level':
        return { pos: 6, look: 8, fov: 6 };
      case 'drift_crane_up':
        return { pos: 2, look: 3, fov: 2.5 };
      case 'drift_reverse_track':
        return { pos: 4, look: 5, fov: 4 };
      case 'drift_360_spin':
        return { pos: 2, look: 3, fov: 2.5 };
      case 'nitro_low_angle':
        return { pos: 8, look: 10, fov: 8 };
      case 'nitro_tunnel_rush':
        return { pos: 10, look: 12, fov: 10 };
      case 'air_hero':
        return { pos: 3, look: 5, fov: 4 };
      case 'air_bullet_time':
        return { pos: 2, look: 3, fov: 2.5 };
      case 'air_ground_anticipation':
        return { pos: 4, look: 5, fov: 4 };
      case 'cop_two_shot':
        return { pos: 5, look: 7, fov: 5 };
      case 'cop_helicopter':
        return { pos: 2, look: 3, fov: 2 };
      case 'cop_bumper_cam':
        return { pos: 8, look: 10, fov: 7 };
      case 'cop_chase_wide':
        return { pos: 3, look: 4, fov: 3 };
      case 'cop_dramatic_pursuit':
        return { pos: 2.5, look: 3.5, fov: 3 };
      case 'impact_reaction':
        return { pos: 25, look: 25, fov: 15 };
      case 'near_miss':
        return { pos: 15, look: 18, fov: 12 };
      case 'speed_rush':
        return { pos: 6, look: 8, fov: 7 };
      case 'speed_flyby':
        return { pos: 3, look: 4, fov: 3 };
      case 'speed_worm_eye':
        return { pos: 5, look: 6, fov: 5 };
      case 'speed_360_orbit':
        return { pos: 2, look: 3, fov: 2.5 };
      case 'hood_cam':
        return { pos: 10, look: 12, fov: 8 };
      case 'tire_orbit':
        return { pos: 8, look: 10, fov: 6 };
      case 'start_tire_reveal':
        if (this.shotTimer < 1.0) return { pos: 12, look: 14, fov: 8 };
        if (this.shotTimer < 2.0) return { pos: 8, look: 10, fov: 6 };
        return { pos: 5, look: 7, fov: 5 };
      case 'helicopter_reveal':
        return { pos: 3, look: 4, fov: 3 };
      case 'helicopter_swoop':
        return { pos: 4, look: 5, fov: 4 };
      case 'helicopter_chase':
        return { pos: 5, look: 6, fov: 4 };
      case 'pursuit_breaker_cam':
        return { pos: 12, look: 14, fov: 10 };
      case 'victory_celebration':
        return { pos: 2.9, look: 3.8, fov: 3.4 };
      default:
        return { pos: 5, look: 7, fov: 6 };
    }
  }

  private getBlur(input: DirectorInput): number {
    if (this.isDriftCinematicShot()) {
      const intensity = Math.min(1, input.driftAngle / 45);
      if (this.currentShot === 'drift_ground_level') return 0.5 + intensity * 0.3;
      if (this.currentShot === 'drift_low_sweep') return 0.45 + intensity * 0.25;
      if (this.currentShot === 'drift_orbit') return 0.35 + intensity * 0.2;
      return 0.3 + intensity * 0.25;
    }
    if (this.isAirCinematicShot()) {
      if (this.currentShot === 'air_bullet_time') return 0.6;
      if (this.currentShot === 'air_ground_anticipation') return 0.5;
      return 0.4;
    }
    if (this.isSpeedCinematicShot()) {
      if (this.currentShot === 'speed_worm_eye') return 0.7;
      if (this.currentShot === 'speed_flyby') return 0.55;
      return 0.4;
    }
    if (this.currentShot === 'near_miss') return 0.5;
    if (this.currentShot === 'nitro_low_angle') return 0.35;
    if (this.currentShot === 'nitro_tunnel_rush') return 0.65;
    if (this.currentShot === 'cop_helicopter') return 0.25;
    if (this.currentShot === 'cop_bumper_cam') return 0.45;
    if (this.currentShot === 'cop_chase_wide') return 0.35;
    if (this.currentShot === 'cop_dramatic_pursuit') return 0.55;
    if (this.currentShot === 'hood_cam') return 0.25;
    if (this.currentShot === 'victory_celebration') {
      return 0.4 + Math.sin(this.shotTimer * 1.9) * 0.16;
    }
    if (input.speedKmh > 120) return 0.12 + (input.speedKmh - 120) / 300;
    return 0.08;
  }

  private getVignette(input: DirectorInput): number {
    if (this.isDriftCinematicShot()) {
      const intensity = Math.min(1, input.driftAngle / 45);
      if (this.currentShot === 'drift_ground_level') return 0.55 + intensity * 0.15;
      if (this.currentShot === 'drift_crane_up') return 0.4 + intensity * 0.2;
      return 0.45 + intensity * 0.15;
    }
    if (this.isAirCinematicShot()) {
      if (this.currentShot === 'air_bullet_time') return 0.6;
      return 0.45;
    }
    if (this.isSpeedCinematicShot()) {
      if (this.currentShot === 'speed_worm_eye') return 0.65;
      if (this.currentShot === 'speed_flyby') return 0.5;
      return 0.45;
    }
    if (this.currentShot === 'near_miss') return 0.6;
    if (this.currentShot === 'nitro_tunnel_rush') return 0.55;
    if (this.currentShot === 'cop_two_shot') return 0.55;
    if (this.currentShot === 'cop_helicopter') return 0.35;
    if (this.currentShot === 'cop_bumper_cam') return 0.5;
    if (this.currentShot === 'cop_chase_wide') return 0.45;
    if (this.currentShot === 'cop_dramatic_pursuit') return 0.6;
    if (this.currentShot === 'nitro_low_angle') return 0.45;
    if (this.currentShot === 'impact_reaction') return 0.5;
    if (this.currentShot === 'victory_celebration') {
      return 0.4 + Math.sin(this.shotTimer * 1.7) * 0.14;
    }
    if (input.speedKmh > 140) return 0.25 + (input.speedKmh - 140) / 400;
    return 0.18;
  }

  getCurrentShot(): ShotType {
    return this.currentShot;
  }

  /** Force a pursuit breaker destruction camera for a dramatic moment. */
  triggerPursuitBreakerCam(breakerX: number, breakerZ: number): void {
    this.pursuitBreakerPos.set(breakerX, 0, breakerZ);
    this.forcedShot = 'pursuit_breaker_cam';
    this.forcedShotDuration = 1.2;
    this.currentShot = 'pursuit_breaker_cam';
    this.shotTimer = 0;
    this.lastShotChange = this.totalTime;
    this.shotHoldTime = 1.2;
  }

  /** Win sequence: low → crane → wide hero, synced with GameView celebration window. */
  triggerVictoryCelebration(): void {
    this.forcedShot = 'victory_celebration';
    this.forcedShotDuration = 3.4;
    this.currentShot = 'victory_celebration';
    this.shotTimer = 0;
    this.lastShotChange = this.totalTime;
    this.shotHoldTime = 3.4;
    this.shakeTrauma = Math.min(1, 0.34);
  }

  /**
   * Pursuit Breaker Destruction Cam:
   * Phase 1 (0-0.4s): Low wide angle from the side, showing car + breaker explosion
   * Phase 2 (0.4-0.8s): Quick orbit around the destruction
   * Phase 3 (0.8-1.2s): Pull out to resume chase
   */
  private shotPursuitBreakerCam(
    carPos: THREE.Vector3,
    vel: THREE.Vector3,
    heading: number,
  ): void {
    const t = this.shotTimer;
    const bx = this.pursuitBreakerPos.x;
    const bz = this.pursuitBreakerPos.z;
    const midX = (carPos.x + bx) * 0.5;
    const midZ = (carPos.z + bz) * 0.5;

    if (t < 0.4) {
      // Phase 1: Low dramatic angle from the side, looking at explosion
      const side = new THREE.Vector3(Math.cos(heading), 0, -Math.sin(heading));
      const sideDir = this.driftSide > 0 ? 1 : -1;
      this.targetPos.set(
        midX + side.x * 12 * sideDir,
        1.2,
        midZ + side.z * 12 * sideDir,
      );
      this.targetLook.set(bx, 2.5, bz);
      this.targetFov = 55;
      this.targetDutch = sideDir * 0.12;
    } else if (t < 0.8) {
      // Phase 2: Orbit around the destruction point
      const phase = (t - 0.4) / 0.4;
      const angle = heading + Math.PI * 0.5 + phase * Math.PI;
      const radius = 8 + phase * 4;
      this.targetPos.set(
        midX + Math.cos(angle) * radius,
        3 + phase * 4,
        midZ + Math.sin(angle) * radius,
      );
      this.targetLook.set(
        midX + vel.x * 0.3,
        1.0,
        midZ + vel.z * 0.3,
      );
      this.targetFov = 50 + phase * 10;
      this.targetDutch = -0.08 * (1 - phase);
    } else {
      // Phase 3: Pull back to resume behind car
      const phase = Math.min(1, (t - 0.8) / 0.4);
      const back = new THREE.Vector3(-Math.sin(heading), 0, -Math.cos(heading));
      const dist = 6 + phase * 6;
      this.targetPos.set(
        carPos.x + back.x * dist,
        2 + phase * 2,
        carPos.z + back.z * dist,
      );
      this.targetLook.copy(carPos).add(new THREE.Vector3(0, 1.2, 0));
      this.targetFov = 60 - phase * 10;
      this.targetDutch = 0;
    }
  }

  /**
   * Extraction win — five beats: impact punch → violent orbit → crane hero →
   * dolly toward goal → wide triumph. Handheld micro-wobble sells adrenaline.
   */
  private shotVictoryCelebration(
    carPos: THREE.Vector3,
    extractionDir: THREE.Vector3,
    heading: number,
  ): void {
    const t = this.shotTimer;
    const ext = this.tmpVictoryExt;
    ext.copy(extractionDir);
    if (ext.lengthSq() < 1e-8) {
      ext.set(Math.sin(heading), 0, Math.cos(heading));
    } else {
      ext.normalize();
    }
    const right = new THREE.Vector3(Math.cos(heading), 0, -Math.sin(heading));
    const back = new THREE.Vector3(-Math.sin(heading), 0, -Math.cos(heading));
    const fwd = new THREE.Vector3(Math.sin(heading), 0, Math.cos(heading));

    const hb = 0.22;
    const hx = Math.sin(t * 13.1 + 0.7) * hb;
    const hy = Math.cos(t * 10.4) * hb * 0.62;
    const hz = Math.sin(t * 11.8 + 1.2) * hb * 0.55;

    const easeOutCubic = (u: number) => 1 - (1 - u) ** 3;
    const smoothstep = (a: number, b: number, x: number) => {
      const k = Math.min(1, Math.max(0, (x - a) / (b - a)));
      return k * k * (3 - 2 * k);
    };

    if (t < 0.52) {
      const u = easeOutCubic(t / 0.52);
      const side = this.driftSide > 0 ? 1 : -1;
      const dist = 5.1 + u * 2.4;
      const height = 0.72 + u * 1.15;
      this.tmpVictorySide.copy(right).multiplyScalar(side * dist * 0.88);
      this.targetPos
        .copy(carPos)
        .addScaledVector(fwd, dist * 0.38)
        .add(this.tmpVictorySide)
        .setY(carPos.y + height);
      this.targetPos.x += hx;
      this.targetPos.y += hy;
      this.targetPos.z += hz;
      this.tmpVictoryLook.copy(carPos).addScaledVector(fwd, 1.2).setY(carPos.y + 0.62);
      this.targetLook.copy(this.tmpVictoryLook);
      const punch = smoothstep(0, 0.52, t);
      this.targetFov = 72 - punch * 19 + Math.sin(t * 14) * 3;
      this.targetDutch = side * (0.11 + u * 0.08) + Math.sin(t * 16) * 0.04;
    } else if (t < 1.38) {
      const u = (t - 0.52) / 0.86;
      const orbit = heading + u * 2.65;
      const dist = 4.85 + Math.sin(u * Math.PI) * 0.65;
      const height = 1.05 + Math.sin(u * Math.PI * 1.5) * 0.55;
      this.targetPos.set(
        carPos.x + Math.cos(orbit) * dist + hx,
        carPos.y + height + hy,
        carPos.z + Math.sin(orbit) * dist + hz,
      );
      this.targetLook.copy(carPos).setY(carPos.y + 0.58);
      this.targetFov = 53 + Math.sin(u * Math.PI * 2) * 11;
      this.targetDutch = Math.sin(orbit * 0.55) * 0.2 + Math.sin(t * 18) * 0.05;
    } else if (t < 2.18) {
      const u = (t - 1.38) / 0.8;
      const e = easeOutCubic(u);
      const orbit = heading + 2.65 * 0.86 + u * 1.15;
      const dist = 5.4 + e * 6.2;
      const height = 1.55 + e * 5.8;
      this.targetPos.set(
        carPos.x + Math.cos(orbit) * dist + hx * 0.7,
        carPos.y + height + hy * 0.8,
        carPos.z + Math.sin(orbit) * dist + hz * 0.7,
      );
      this.tmpVictoryLook
        .copy(carPos)
        .addScaledVector(ext, 10 + e * 8)
        .setY(carPos.y + 1.1 + e * 1.8);
      this.targetLook.copy(this.tmpVictoryLook);
      this.targetFov = 51 - e * 9 + Math.sin(t * 5) * 4;
      this.targetDutch = Math.sin(t * 2.8) * 0.12;
    } else if (t < 2.88) {
      const u = (t - 2.18) / 0.7;
      const e = easeOutCubic(u);
      const side = ext.dot(right) >= 0 ? 1 : -1;
      this.targetPos
        .copy(carPos)
        .addScaledVector(ext, -8 - e * 10)
        .addScaledVector(right, side * (5 + e * 7))
        .addScaledVector(back, -4 - e * 5)
        .setY(carPos.y + 2.4 + e * 2.2);
      this.targetPos.x += hx * 0.85;
      this.targetPos.z += hz * 0.85;
      this.tmpVictoryLook.set(
        carPos.x + ext.x * 22,
        carPos.y + 1.4,
        carPos.z + ext.z * 22,
      );
      this.targetLook.copy(carPos).lerp(this.tmpVictoryLook, 0.55 + e * 0.35);
      this.targetFov = 44 - e * 6;
      this.targetDutch = side * 0.07 * (1 - e);
    } else {
      const u = Math.min(1, (t - 2.88) / 0.52);
      const e = easeOutCubic(u);
      const side = ext.dot(right) >= 0 ? 1 : -1;
      this.targetPos
        .copy(carPos)
        .addScaledVector(ext, -14 - e * 6)
        .addScaledVector(right, side * (11 + e * 4))
        .addScaledVector(back, -12 - e * 5)
        .setY(carPos.y + 4.2 + e * 1.8);
      this.targetPos.x += hx * 0.5;
      this.targetPos.z += hz * 0.5;
      this.targetLook.set(
        carPos.x + ext.x * 16,
        carPos.y + 1.25,
        carPos.z + ext.z * 16,
      );
      this.targetFov = 58 + e * 12 + Math.sin(t * 3.1) * 2;
      this.targetDutch = side * 0.04 * (1 - e);
    }
  }

  /**
   * Tire Wheel-Cam Shot - Camera appears mounted ON the spinning tire
   * Phase 1: Camera spins WITH the tire (like GoPro mounted on wheel)
   * Phase 2: Camera pulls back while still spinning to reveal the car
   * Inspired by Volkswagen wheel-mounted camera commercials
   */
  private shotTireOrbit(
    pos: THREE.Vector3,
    _right: THREE.Vector3,
    heading: number,
    vel: THREE.Vector3,
    driftAngle: number,
  ): void {
    const t = this.shotTimer;
    const speed = vel.length();
    const intensity = Math.min(1, driftAngle / 45);

    const wheelRadius = 0.43;
    const wheelSpinSpeed = speed / wheelRadius;
    this.tireOrbitAngle += wheelSpinSpeed * 0.016;

    const tireOffsetSide = this.driftSide * 1.2;
    const tireOffsetBack = -1.6;
    const tireHeight = 0.43;

    const fwdX = Math.sin(heading);
    const fwdZ = Math.cos(heading);
    const rightX = Math.cos(heading);
    const rightZ = -Math.sin(heading);

    const tireX = pos.x + rightX * tireOffsetSide + fwdX * tireOffsetBack;
    const tireZ = pos.z + rightZ * tireOffsetSide + fwdZ * tireOffsetBack;
    const tireY = pos.y + tireHeight;

    const phase1Duration = 1.2;
    const phase2Duration = 1.0;

    let camRadius: number;
    let camHeight: number;
    let lookTarget: THREE.Vector3;
    let fov: number;

    if (t < phase1Duration) {
      const p = t / phase1Duration;
      camRadius = 0.5 + p * 0.3;
      camHeight = 0;
      
      const spinX = Math.cos(this.tireOrbitAngle) * camRadius;
      const spinY = Math.sin(this.tireOrbitAngle) * camRadius;
      
      const camLocalX = spinX;
      const camLocalY = spinY;
      
      const camWorldX = tireX + rightX * camLocalX;
      const camWorldZ = tireZ + rightZ * camLocalX;
      const camWorldY = tireY + camLocalY;
      
      this.targetPos.set(camWorldX, camWorldY, camWorldZ);
      
      const lookAhead = vel.clone().normalize().multiplyScalar(2);
      lookTarget = new THREE.Vector3(
        tireX + lookAhead.x,
        tireY,
        tireZ + lookAhead.z,
      );
      
      fov = 90 + intensity * 20;
      
      const spinPhase = Math.sin(this.tireOrbitAngle);
      this.targetDutch = spinPhase * 0.8;
      
    } else {
      const p = Math.min(1, (t - phase1Duration) / phase2Duration);
      const easeOut = 1 - Math.pow(1 - p, 3);
      
      camRadius = 0.8 + easeOut * 4;
      camHeight = easeOut * 2;
      
      const slowSpin = this.tireOrbitAngle * (1 - easeOut * 0.7);
      const spinX = Math.cos(slowSpin) * camRadius;
      const spinY = Math.sin(slowSpin) * camRadius * (1 - easeOut * 0.8);
      
      const camLocalX = spinX;
      const camLocalY = spinY + camHeight;
      
      const camWorldX = tireX + rightX * camLocalX - fwdX * easeOut * 2;
      const camWorldZ = tireZ + rightZ * camLocalX - fwdZ * easeOut * 2;
      const camWorldY = tireY + camLocalY;
      
      this.targetPos.set(camWorldX, Math.max(0.5, camWorldY), camWorldZ);
      
      lookTarget = new THREE.Vector3(
        tireX * (1 - easeOut) + pos.x * easeOut,
        tireY * (1 - easeOut) + (pos.y + 0.5) * easeOut,
        tireZ * (1 - easeOut) + pos.z * easeOut,
      );
      
      fov = 90 - easeOut * 30 + intensity * 10;
      
      this.targetDutch = (1 - easeOut) * Math.sin(slowSpin) * 0.5 + 
                         easeOut * intensity * 0.15 * this.driftSide;
    }

    this.targetLook.copy(lookTarget);
    this.targetFov = fov;
  }

  /**
   * Start Tire Reveal Shot - Cinematic intro when car first starts moving
   * 
   * EXTREME CLOSE-UP of the spinning tire that FILLS THE ENTIRE SCREEN
   * Camera is positioned OUTSIDE the tire, at ground level, looking AT the tire
   * Then DOLLY OUT to reveal the full car
   * 
   * Phase 1: Extreme close-up - tire fills the entire frame, camera at ground level
   * Phase 2: Dolly out - camera pulls back smoothly to reveal the car
   */
  private shotStartTireReveal(
    pos: THREE.Vector3,
    _right: THREE.Vector3,
    heading: number,
    vel: THREE.Vector3,
    _speedKmh: number,
  ): void {
    const t = this.shotTimer;

    const tireOffsetSide = 1.3;
    const tireOffsetBack = -1.5;
    const tireHeight = 0.43;

    const fwdX = Math.sin(heading);
    const fwdZ = Math.cos(heading);
    const rightX = Math.cos(heading);
    const rightZ = -Math.sin(heading);

    const tireX = pos.x + rightX * tireOffsetSide + fwdX * tireOffsetBack;
    const tireZ = pos.z + rightZ * tireOffsetSide + fwdZ * tireOffsetBack;
    const tireY = pos.y + tireHeight;

    const phase1Duration = 1.8;
    const phase2Duration = 2.0;

    let camX: number, camY: number, camZ: number;
    let fov: number;

    if (t < phase1Duration) {
      const camDistFromTire = 0.8;
      const camHeight = 0.25;
      
      camX = tireX + rightX * camDistFromTire;
      camZ = tireZ + rightZ * camDistFromTire;
      camY = camHeight;
      
      fov = 35;
      
      const breathe = Math.sin(t * 2) * 0.02;
      camX += rightX * breathe;
      camZ += rightZ * breathe;
      
      this.targetDutch = Math.sin(t * 1.5) * 0.03;
      
    } else {
      const p = Math.min(1, (t - phase1Duration) / phase2Duration);
      const easeOut = 1 - Math.pow(1 - p, 3);
      
      const startDist = 0.8;
      const endDist = 10;
      const camDist = startDist + easeOut * (endDist - startDist);
      
      const startHeight = 0.25;
      const endHeight = 3.5;
      const camHeight = startHeight + easeOut * (endHeight - startHeight);
      
      const sideBlend = 1 - easeOut;
      const backBlend = easeOut;
      
      camX = tireX + rightX * camDist * sideBlend - fwdX * camDist * backBlend;
      camZ = tireZ + rightZ * camDist * sideBlend - fwdZ * camDist * backBlend;
      camY = camHeight;
      
      const startFov = 35;
      const endFov = 60;
      fov = startFov + easeOut * (endFov - startFov);
      
      this.targetDutch = (1 - easeOut) * 0.03;
    }

    const velOffset = vel.clone().multiplyScalar(0.02);
    this.targetPos.set(
      camX + velOffset.x,
      Math.max(0.15, camY),
      camZ + velOffset.z,
    );
    
    if (t < phase1Duration) {
      this.targetLook.set(tireX, tireY, tireZ);
    } else {
      const p = Math.min(1, (t - phase1Duration) / phase2Duration);
      const easeOut = 1 - Math.pow(1 - p, 3);
      
      const lookX = tireX * (1 - easeOut) + pos.x * easeOut;
      const lookY = tireY * (1 - easeOut) + (pos.y + 0.5) * easeOut;
      const lookZ = tireZ * (1 - easeOut) + pos.z * easeOut;
      this.targetLook.set(lookX, lookY, lookZ);
    }
    
    this.targetFov = fov;
  }
}
